import React, { PropTypes } from "react";
import { connect } from "react-redux";
import { Field, reduxForm, formValueSelector } from "redux-form";
import { FormattedMessage, FormattedHTMLMessage } from "react-intl";

import { BSModal } from "../../../../common";
import {
  EndUserFormMessageConfiguration,
  StyledCheckboxInput,
} from "../../../../common/form";
import BasicWithdrawalEmailTemplate from "./BasicWithdrawalEmailTemplate";

import {
  PERMANENT_DEACTIVATION,
  ANNUAL_DEACTIVATION,
  SESSION_DEACTIVATION,
} from "../members/member/coverage/enums/memberCoverageInactivityReasons";
import { MANUAL } from "../../../clients/enums/billingPeriodTypes";
import { PM } from "../../../clients/enums/businessAccountTypes";
import validationUtils from "../../../../../utils/ValidationUtils";
import { WITHDRAWAL_CONFIGURATION_VALIDATION_RULES } from "./withdrawalConfigurationValidationRules";
import {
  WITHDRAWAL_FORM_MESSAGE_VALIDATION_RULES,
  WITHDRAWAL_FORM_POPUP_VALIDATION_RULES,
  WITHDRAWAL_FORM_REDIRECT_BUTTON_MESSAGE_VALIDATION_RULES,
} from "./WithdrawalFormMessageValidationRules";

import MessageBox from "../../../../common/MessageBox";

const extractLocalization = (localizations, localizedFieldName) => {
  const extractedLocalization = Object.keys(localizations).reduce(
    (acc, lang) => {
      return {
        ...acc,
        [lang]: {
          [localizedFieldName]: localizations[lang][localizedFieldName],
        },
      };
    },
    {}
  );

  return {
    localizations: extractedLocalization,
  };
};

class BasicWithdrawalConfigurationFields extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      showPermanentWithdrawalModal: false,
      showAnnualWithdrawalModal: false,
      showSessionWithdrawalModal: false,
      hasSessionWithdrawalForm: false,
      hasAnnualWithdrawalForm: false,
      hasPermanentWithdrawalForm: false,
    };

    this.closeEmailModal = this.closeEmailModal.bind(this);
    this.openEmailModal = this.openEmailModal.bind(this);
    this.handleSessionWithdrawalUpdate = this.handleSessionWithdrawalUpdate.bind(
      this
    );
    this.handleAnnualWithdrawalUpdate = this.handleAnnualWithdrawalUpdate.bind(
      this
    );
    this.handlePermanentWithdrawalUpdate = this.handlePermanentWithdrawalUpdate.bind(
      this
    );
  }

  getChildContext() {
    return {
      scene: this.props.scene,
    };
  }
  componentDidMount() {
    this.setState({
      hasAnnualWithdrawalForm: this.props.initialValues.hasAnnualWithdrawalForm,
      hasPermanentWithdrawalForm: this.props.initialValues
        .hasPermanentWithdrawalForm,
      hasSessionWithdrawalForm: this.props.initialValues
        .hasSessionWithdrawalForm,
    });
  }
  closeEmailModal() {
    this.setState({
      showPermanentWithdrawalModal: false,
      showAnnualWithdrawalModal: false,
      showSessionWithdrawalModal: false,
    });
  }

  openEmailModal(target) {
    switch (target) {
      case "ANNUAL_DEACTIVATION": {
        this.setState({ showAnnualWithdrawalModal: true });
        break;
      }
      case "PERMANENT_DEACTIVATION": {
        this.setState({ showPermanentWithdrawalModal: true });
        break;
      }
      case "SESSION_DEACTIVATION": {
        this.setState({ showSessionWithdrawalModal: true });
        break;
      }
    }
  }

  handleSessionWithdrawalUpdate(event) {
    event.target.checked
      ? this.setState({ hasSessionWithdrawalForm: true })
      : this.setState({ hasSessionWithdrawalForm: false });
  }

  handleAnnualWithdrawalUpdate(event) {
    event.target.checked
      ? this.setState({ hasAnnualWithdrawalForm: true })
      : this.setState({ hasAnnualWithdrawalForm: false });
  }

  handlePermanentWithdrawalUpdate(event) {
    event.target.checked
      ? this.setState({ hasPermanentWithdrawalForm: true })
      : this.setState({ hasPermanentWithdrawalForm: false });
  }

  render() {
    const { onChange, hasRelatedBillingPeriods } = this.props;
    const { localizations } = this.props.initialValues;
    const isValidToDisplayWithdrawalSession =
      this.props.initialValues.clientBillingPeriodType == MANUAL.value &&
      this.props.initialValues.businessAccountType == PM.value;
    return (
      <form
        onSubmit={this.props.handleSubmit}
        className="withdrawal-configuration configuration-form"
      >
        <h3>
          <FormattedMessage id="clients.client.pages.withdrawal" />
        </h3>
        <Field
          id="hasWithdrawalForm"
          name="hasWithdrawalForm"
          component={StyledCheckboxInput}
          groupClassName="inverted-label"
          type="checkbox"
          checkbox={{ value: true }}
          props={{ onChange }}
          label={
            <FormattedHTMLMessage id="withdrawalConfiguration.form.createWithdrawalForm" />
          }
          normalize={(value) => !!value}
        />
        {this.props.hasWithdrawalForm && (
          <div>
            <div className="indented">
              <Field
                id="mustIncludeWithdrawalCode"
                name="mustIncludeWithdrawalCode"
                component={StyledCheckboxInput}
                type="checkbox"
                checkbox={{ value: true }}
                props={{ onChange }}
                label={
                  <FormattedHTMLMessage id="withdrawalConfiguration.form.includeCodeAuthentication" />
                }
              />
              <Field
                id="hasWithdrawalRefund"
                name="hasWithdrawalRefund"
                component={StyledCheckboxInput}
                type="checkbox"
                checkbox={{ value: true }}
                props={{ onChange }}
                label={
                  <FormattedMessage id="withdrawalConfiguration.form.requireBankingInformation" />
                }
              />
              <Field
                id="refuteWithdrawalForUnpaidCoverages"
                name="refuteWithdrawalForUnpaidCoverages"
                component={StyledCheckboxInput}
                type="checkbox"
                checkbox={{ value: true }}
                props={{ onChange }}
                label={
                  <FormattedMessage id="withdrawalConfiguration.form.refuteWithdrawalForUnpaidCoverages" />
                }
              />
              <Field
                id="mustUseInstitutionEmailForWithdrawal"
                name="mustUseInstitutionEmailForWithdrawal"
                component={StyledCheckboxInput}
                type="checkbox"
                checkbox={{ value: true }}
                props={{ onChange }}
                label={
                  <FormattedMessage id="withdrawalConfiguration.form.mustUseInstitutionEmailForWithdrawal" />
                }
              />
              <Field
                id="hasWithdrawalFormMessage"
                name="hasWithdrawalFormMessage"
                component={StyledCheckboxInput}
                type="checkbox"
                checkbox={{ value: true }}
                props={{ onChange }}
                label={
                  <FormattedMessage id="withdrawalConfiguration.form.hasWithdrawalFormMessage" />
                }
                normalize={(value) => !!value}
              />
              {this.props.hasWithdrawalFormMessage && (
                <EndUserFormMessageConfiguration
                  form="withdrawalMessageConfiguration"
                  initialValues={extractLocalization(
                    localizations,
                    "withdrawalFormMessage"
                  )}
                  localizedFieldName="withdrawalFormMessage"
                  onSubmit={this.props.updateMessageConfiguration}
                  validationRules={WITHDRAWAL_FORM_MESSAGE_VALIDATION_RULES}
                />
              )}
              <Field
                id="hasWithdrawalPopupMessage"
                name="hasWithdrawalPopupMessage"
                component={StyledCheckboxInput}
                type="checkbox"
                checkbox={{ value: true }}
                props={{ onChange }}
                label={
                  <FormattedMessage id="withdrawalConfiguration.form.hasWithdrawalPopupMessage" />
                }
                normalize={(value) => !!value}
              />
              {this.props.hasWithdrawalPopupMessage && (
                <div>
                  <EndUserFormMessageConfiguration
                    formTitle={
                      <h5>
                        <FormattedMessage id="withdrawalConfiguration.form.withdrawalPopupTitle" />
                      </h5>
                    }
                    form="withdrawalPopupTitleConfiguration"
                    initialValues={extractLocalization(
                      localizations,
                      "withdrawalPopupTitle"
                    )}
                    localizedFieldName="withdrawalPopupTitle"
                    onSubmit={this.props.updateMessageConfiguration}
                    validationRules={WITHDRAWAL_FORM_POPUP_VALIDATION_RULES}
                  />
                  <EndUserFormMessageConfiguration
                    formTitle={
                      <h5>
                        <FormattedMessage id="withdrawalConfiguration.form.withdrawalPopupMessage" />
                      </h5>
                    }
                    fieldType="textarea"
                    form="withdrawalPopupMessageConfiguration"
                    initialValues={extractLocalization(
                      localizations,
                      "withdrawalPopupMessage"
                    )}
                    localizedFieldName="withdrawalPopupMessage"
                    onSubmit={this.props.updateMessageConfiguration}
                    validationRules={WITHDRAWAL_FORM_POPUP_VALIDATION_RULES}
                  />
                </div>
              )}
              <Field
                id="hasWithdrawalRedirectButton"
                name="hasWithdrawalRedirectButton"
                component={StyledCheckboxInput}
                type="checkbox"
                checkbox={{ value: true }}
                props={{ onChange }}
                label={
                  <FormattedMessage id="withdrawalConfiguration.form.hasWithdrawalRedirectButton" />
                }
                normalize={(value) => !!value}
              />
              {this.props.hasWithdrawalRedirectButton && (
                <div>
                  <EndUserFormMessageConfiguration
                    fieldType="textarea"
                    formTitle={
                      <h5>
                        <FormattedMessage id="withdrawalConfiguration.form.redirectButtonMessage" />
                      </h5>
                    }
                    form="redirectButtonMessageConfiguration"
                    initialValues={extractLocalization(
                      localizations,
                      "withdrawalRedirectButtonTitle"
                    )}
                    localizedFieldName="withdrawalRedirectButtonTitle"
                    onSubmit={this.props.updateMessageConfiguration}
                    validationRules={
                      WITHDRAWAL_FORM_REDIRECT_BUTTON_MESSAGE_VALIDATION_RULES
                    }
                  />
                  <EndUserFormMessageConfiguration
                    formTitle={
                      <h5>
                        <FormattedMessage id="withdrawalConfiguration.form.redirectButtonLabel" />
                      </h5>
                    }
                    form="redirectButtonLabelConfiguration"
                    initialValues={extractLocalization(
                      localizations,
                      "withdrawalRedirectButtonValue"
                    )}
                    localizedFieldName="withdrawalRedirectButtonValue"
                    onSubmit={this.props.updateMessageConfiguration}
                    validationRules={
                      WITHDRAWAL_FORM_REDIRECT_BUTTON_MESSAGE_VALIDATION_RULES
                    }
                  />
                  <EndUserFormMessageConfiguration
                    formTitle={
                      <h5>
                        <FormattedMessage id="withdrawalConfiguration.form.redirectButtonURL" />
                      </h5>
                    }
                    form="redirectButtonURLConfiguration"
                    initialValues={extractLocalization(
                      localizations,
                      "withdrawalRedirectUrl"
                    )}
                    localizedFieldName="withdrawalRedirectUrl"
                    onSubmit={this.props.updateMessageConfiguration}
                    validationRules={
                      WITHDRAWAL_FORM_REDIRECT_BUTTON_MESSAGE_VALIDATION_RULES
                    }
                  />
                </div>
              )}
            </div>
            <hr />
            <h4>
              <FormattedMessage id="withdrawalConfiguration.supportedWithdrawals" />
            </h4>
            <div className="indented">
              <div className="field-wrapper">
                <Field
                  id="hasPermanentWithdrawalForm"
                  name="hasPermanentWithdrawalForm"
                  component={StyledCheckboxInput}
                  type="checkbox"
                  checkbox={{ value: true }}
                  props={{ onChange }}
                  disabled={this.state.hasSessionWithdrawalForm}
                  onChange={this.handlePermanentWithdrawalUpdate}
                  label={
                    <FormattedMessage id="withdrawalConfiguration.form.allowPermanentDeactivation" />
                  }
                />
                {this.props.initialValues.hasPermanentWithdrawalForm && (
                  <div className="view-email">
                    <a
                      onClick={this.openEmailModal.bind(
                        this,
                        PERMANENT_DEACTIVATION.value
                      )}
                    >
                      <span className="icon-eye" />
                    </a>
                    <BSModal
                      show={this.state.showPermanentWithdrawalModal}
                      title={
                        <FormattedMessage id="withdrawalConfiguration.form.permanentDeactivationEmailTemplate" />
                      }
                      onClose={this.closeEmailModal}
                    >
                      <BasicWithdrawalEmailTemplate
                        clientId={this.props.clientId}
                        isInactiveReasonType={PERMANENT_DEACTIVATION.value}
                      />
                    </BSModal>
                  </div>
                )}
              </div>
              <div className="field-wrapper">
                <Field
                  id="hasAnnualWithdrawalForm"
                  name="hasAnnualWithdrawalForm"
                  component={StyledCheckboxInput}
                  type="checkbox"
                  checkbox={{ value: true }}
                  disabled={this.state.hasSessionWithdrawalForm}
                  props={{ onChange }}
                  onChange={this.handleAnnualWithdrawalUpdate}
                  label={
                    <FormattedMessage id="withdrawalConfiguration.form.allowYearlyDeactivation" />
                  }
                />
                {this.props.initialValues.hasAnnualWithdrawalForm && (
                  <div className="view-email">
                    <a
                      onClick={this.openEmailModal.bind(
                        this,
                        ANNUAL_DEACTIVATION.value
                      )}
                    >
                      <span className="icon-eye" />
                    </a>
                    <BSModal
                      show={this.state.showAnnualWithdrawalModal}
                      title={
                        <FormattedMessage id="withdrawalConfiguration.form.yearlyDeactivationEmailTemplate" />
                      }
                      onClose={this.closeEmailModal}
                    >
                      <BasicWithdrawalEmailTemplate
                        clientId={this.props.clientId}
                        isInactiveReasonType={ANNUAL_DEACTIVATION.value}
                      />
                    </BSModal>
                  </div>
                )}
              </div>
              {this.state.hasSessionWithdrawalForm &&
                isValidToDisplayWithdrawalSession && (
                  <MessageBox className="alert-info">
                    <FormattedMessage id="billingPeriods.form.warnings.withdrawalOrOtherWithdrawal" />
                  </MessageBox>
                )}
              {hasRelatedBillingPeriods && isValidToDisplayWithdrawalSession && (
                <MessageBox className="alert-info">
                  <FormattedMessage id="billingPeriods.form.warnings.relatedBillingPeriod" />
                </MessageBox>
              )}
              {isValidToDisplayWithdrawalSession && (
                <div className="field-wrapper">
                  <Field
                    id="hasSessionWithdrawalForm"
                    name="hasSessionWithdrawalForm"
                    component={StyledCheckboxInput}
                    type="checkbox"
                    checkbox={{ value: true }}
                    disabled={
                      this.state.hasPermanentWithdrawalForm ||
                      this.state.hasAnnualWithdrawalForm ||
                      hasRelatedBillingPeriods
                    }
                    props={{ onChange }}
                    onChange={this.handleSessionWithdrawalUpdate}
                    label={
                      <FormattedMessage id="withdrawalConfiguration.form.allowSessionallyDeactivation" />
                    }
                  />
                  {this.props.initialValues.hasSessionWithdrawalForm && (
                    <div className="view-email">
                      <a
                        onClick={this.openEmailModal.bind(
                          this,
                          SESSION_DEACTIVATION.value
                        )}
                      >
                        <span className="icon-eye" />
                      </a>
                      <BSModal
                        show={this.state.showSessionWithdrawalModal}
                        title={
                          <FormattedMessage id="withdrawalConfiguration.form.sessionallyDeactivationEmailTemplate" />
                        }
                        onClose={this.closeEmailModal}
                      >
                        <BasicWithdrawalEmailTemplate
                          clientId={this.props.clientId}
                          isInactiveReasonType={SESSION_DEACTIVATION.value}
                        />
                      </BSModal>
                    </div>
                  )}
                </div>
              )}
            </div>
            <hr />
          </div>
        )}
      </form>
    );
  }
}

BasicWithdrawalConfigurationFields.propTypes = {
  clientId: PropTypes.number,
  initialValues: PropTypes.object,
  onChange: PropTypes.func,
  hasWithdrawalForm: PropTypes.bool,
  hasWithdrawalFormMessage: PropTypes.bool,
  hasWithdrawalPopupMessage: PropTypes.bool,
  hasWithdrawalRedirectButton: PropTypes.bool,
  handleSubmit: PropTypes.func,
  scene: PropTypes.string,
  validation: PropTypes.object,
  updateMessageConfiguration: PropTypes.func,
  lockMessageForm: PropTypes.func,
  unlockMessageForm: PropTypes.func,
  readOnlyMessage: PropTypes.bool,
  businessAccountType: PropTypes.number,
  clientBillingPeriodType: PropTypes.string,
  hasRelatedBillingPeriods: PropTypes.bool,
};

BasicWithdrawalConfigurationFields.defaultProps = {
  scene: "",
};

BasicWithdrawalConfigurationFields.childContextTypes = {
  scene: PropTypes.string,
};

function mapStateToProps(state) {
  const selector = formValueSelector("withdrawalConfiguration");

  return {
    hasWithdrawalForm: selector(state, "hasWithdrawalForm"),
    hasWithdrawalFormMessage: selector(state, "hasWithdrawalFormMessage"),
    hasWithdrawalPopupMessage: selector(state, "hasWithdrawalPopupMessage"),
    hasWithdrawalRedirectButton: selector(state, "hasWithdrawalRedirectButton"),
  };
}

export default connect(mapStateToProps, null, null, { withRef: true })(
  reduxForm({
    form: "withdrawalConfiguration",
    validate: (values) =>
      validationUtils.validate(
        values,
        WITHDRAWAL_CONFIGURATION_VALIDATION_RULES
      ),
  })(BasicWithdrawalConfigurationFields)
);



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/withdrawal-configuration/BasicWithdrawalConfigurationFields.js